import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { ConnectionEngineIcon } from '@/features/connections/components/ConnectionEngineIcon';
import { useConnectionStore } from '@/features/connections/store/connectionStore';
import { useWorkspaceStore } from '@/features/workspace/core/store/workspaceStore';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useLazyConnectOnDemand } from '@/features/sessions/hooks/useLazyConnectOnDemand';
import { useSessionStore } from '@/features/sessions/store/sessionStore';
import { useActiveTabConnectionNeed } from '../hooks/useActiveTabConnectionNeed';
import { usePendingRetryStore } from '../store/pendingRetryStore';
import type { BannerConnectionRef } from '../types';

// ---------------------------------------------------------------------------
// BannerRow — one row per connection ref
// ---------------------------------------------------------------------------

type BannerRowProps = {
  connectionRef: BannerConnectionRef;
  sideLabel?: 'Source' | 'Target';
  retryLabel: string | null;
  /** All connectionIds that must be active for the pending retry to fire. */
  retryRequires: string[];
  onConnected: () => void;
};

function BannerRow({
  connectionRef,
  sideLabel,
  retryLabel,
  retryRequires,
  onConnected,
}: BannerRowProps) {
  const connection = useConnectionStore((s) =>
    s.connections.find((c) => c.id === connectionRef.connectionId),
  );
  const connectToDatabase = useConnectionStore((s) => s.connectToDatabase);
  const isActive = useConnectionStore((s) =>
    s.activeConnections.some(
      (a) => a.id === connectionRef.connectionId && a.status === 'connected',
    ),
  );
  // A connect started elsewhere — lazy connect on resume firing as the user
  // lands on this tab, or the Object Explorer's reconnect. Without this the
  // banner offers a Connect button for something already connecting.
  const isConnecting = useConnectionStore(
    (s) => s.connectionOperationById?.[connectionRef.connectionId] === 'connecting',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { track } = useAnalytics();

  if (isActive) return null;

  const buttonLabel = retryLabel ? `Connect and ${retryLabel}` : 'Connect';

  const handleConnect = async () => {
    if (!connection) {
      setError('Connection not found');
      return;
    }
    setBusy(true);
    setError(null);
    track('feature:connection_banner_connect_clicked', {
      withRetry: retryLabel !== null,
    });
    try {
      await connectToDatabase(connection);
      // Re-read activeConnections fresh after the connect attempt
      const freshActive = useConnectionStore.getState().activeConnections;
      const nowActive = freshActive.some(
        (a) => a.id === connectionRef.connectionId && a.status === 'connected',
      );
      if (nowActive) {
        // Clear the session's stale `disconnected` verdict. Since lazy connect
        // on resume, a connection can legitimately sit at that status without
        // ever having failed — and surfaces like the query toolbar's execute
        // button read it as authoritative, so the tab would stay unusable
        // behind a banner that just disappeared.
        useSessionStore
          .getState()
          .updateConnectionStatus(connectionRef.connectionId, 'connected');
        // Check if ALL required connections are now active before firing retry
        const allRequired = retryRequires.every((id) =>
          useConnectionStore
            .getState()
            .activeConnections.some((a) => a.id === id && a.status === 'connected'),
        );
        if (allRequired) {
          onConnected();
        }
      } else {
        setError('Failed to connect');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Connect failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="workspace-connection-banner-row"
      className="flex items-center gap-2 pl-3 pr-9 py-1 text-xs"
    >
      <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
      {connection?.color && (
        <span
          className="h-2 w-2 rounded-full shrink-0"
          style={{ backgroundColor: connection.color }}
        />
      )}
      <ConnectionEngineIcon engine={connectionRef.engine} size="sm" iconOnly />
      <span className="font-medium">
        {sideLabel ? (
          <span className="text-muted-foreground mr-1">{sideLabel}:</span>
        ) : null}
        {connectionRef.label}
      </span>
      <span className="text-muted-foreground">
        {isConnecting && !busy ? 'is connecting…' : 'is not active.'}
      </span>
      {error && <span className="text-destructive ml-2">{error}</span>}
      <div className="flex-1" />
      <Button
        variant="outline"
        size="sm"
        className="h-6 text-xs"
        onClick={handleConnect}
        disabled={busy || isConnecting}
      >
        {busy || isConnecting ? <RandomDotmSpinner size={12} /> : buttonLabel}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WorkspaceConnectionBanner — shell banner mounted once in workspace
// ---------------------------------------------------------------------------

export function WorkspaceConnectionBanner() {
  const need = useActiveTabConnectionNeed();
  // Lazy connect on resume: a tab whose connection the resume deliberately
  // left closed opens it the moment the user lands on the tab. Mounted here
  // because this component already tracks "what does the active tab need" —
  // one subscription, one answer, and the banner is the visible half of the
  // same state.
  useLazyConnectOnDemand(need);
  const activeTabId = useWorkspaceStore((s) => s.activeTabId);
  const pending = usePendingRetryStore((s) =>
    activeTabId ? (s.pending[activeTabId] ?? null) : null,
  );
  const clearPending = usePendingRetryStore((s) => s.clearPendingRetry);
  const [dismissedTabId, setDismissedTabId] = useState<string | null>(null);

  // Must be called unconditionally (Rules of Hooks) — placed before any early returns.
  // Uses a reactive selector so re-renders trigger when activeConnections changes.
  const everyActive = useConnectionStore((s) => {
    if (need.kind === 'single') {
      return s.activeConnections.some(
        (a) => a.id === need.ref.connectionId && a.status === 'connected',
      );
    }
    if (need.kind === 'dual') {
      return (
        s.activeConnections.some(
          (a) => a.id === need.source.connectionId && a.status === 'connected',
        ) &&
        s.activeConnections.some(
          (a) => a.id === need.target.connectionId && a.status === 'connected',
        )
      );
    }
    return false;
  });

  if (need.kind === 'none') return null;
  if (!activeTabId) return null;
  if (dismissedTabId === activeTabId) return null;
  if (everyActive) return null;

  const handleConnected = () => {
    if (pending) {
      void pending.action();
      clearPending(pending.tabId);
    }
  };

  const handleDismiss = () => {
    if (pending) clearPending(pending.tabId);
    setDismissedTabId(activeTabId);
  };

  const retryLabel = pending?.label ?? null;
  const retryRequires = pending?.requires ?? [];

  return (
    <div
      data-testid="workspace-connection-banner"
      className="relative border-b border-amber-300/50 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700/60"
    >
      {need.kind === 'single' ? (
        <BannerRow
          connectionRef={need.ref}
          retryLabel={retryLabel}
          retryRequires={retryRequires}
          onConnected={handleConnected}
        />
      ) : (
        <>
          <BannerRow
            connectionRef={need.source}
            sideLabel="Source"
            retryLabel={retryLabel}
            retryRequires={retryRequires}
            onConnected={handleConnected}
          />
          <BannerRow
            connectionRef={need.target}
            sideLabel="Target"
            retryLabel={retryLabel}
            retryRequires={retryRequires}
            onConnected={handleConnected}
          />
        </>
      )}
      {/* Single dismiss button for the whole banner, positioned in top-right corner */}
      <button
        aria-label="Dismiss"
        className="absolute top-0 right-0 px-2 py-1 text-muted-foreground hover:text-foreground"
        onClick={handleDismiss}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
